import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { brand, radius } from "../../theme/businessFlowsTheme";

// Where the reader is, as the masthead of Home rather than a grey line under
// the greeting. Three facts decide what they are looking at - the workspace,
// the library, and the view that library is read through - so all three are
// labelled and legible, and the library carries the brand's yellow mark
// because it is the one of the three the reader changes most often.
//
// The view keeps the exact wording every other screen uses
// (utils/viewContext.js viewContextLine): "View: Lean", or the plain
// sentence where none applies. One place knows those words; this only shows
// them.

// A small capitalised label over a fact. The page's hierarchy comes from
// type - this is the quietest step of it.
export const eyebrowSx = {
  fontSize: "0.69rem",
  fontWeight: 650,
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  color: brand.muted,
  lineHeight: 1.4,
};

function Fact({ label, children, marked }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="p" sx={eyebrowSx}>
        {label}
      </Typography>
      <Typography
        component="p"
        sx={{
          mt: 0.25,
          fontWeight: 650,
          fontSize: { xs: "1rem", md: "1.05rem" },
          color: brand.ink,
          overflowWrap: "anywhere",
          // The mark: a yellow rule under the name, the same gesture the
          // wordmark uses. Never a fill behind the words.
          ...(marked
            ? { boxShadow: `inset 0 -0.18em 0 ${brand.signal}`, display: "inline", lineHeight: 1.5 }
            : {}),
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export default function ContextBand({ workspaceName, databaseName, viewText, viewNamed }) {
  if (!workspaceName && !databaseName && !viewText) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: { xs: "flex-start", sm: "center" },
        columnGap: { xs: 2, md: 3 },
        rowGap: 1.5,
        mt: { xs: 1.5, md: 2 },
      }}
    >
      {workspaceName && <Fact label="Workspace">{workspaceName}</Fact>}
      {workspaceName && databaseName && (
        <Box aria-hidden="true" sx={{ alignSelf: "stretch", width: "1px", bgcolor: brand.line, display: { xs: "none", sm: "block" } }} />
      )}
      {databaseName && (
        <Fact label="Library" marked>
          {databaseName}
        </Fact>
      )}
      {viewText && (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            borderRadius: radius.control,
            bgcolor: viewNamed ? brand.markSoft : brand.shell,
            border: `1px solid ${viewNamed ? "transparent" : brand.line}`,
            maxWidth: "100%",
            ml: { sm: "auto" },
          }}
        >
          <VisibilityRoundedIcon fontSize="small" sx={{ color: viewNamed ? brand.signalDeep : brand.muted }} />
          <Typography variant="body2" sx={{ color: viewNamed ? brand.ink : brand.muted, fontWeight: viewNamed ? 600 : 400, overflowWrap: "anywhere" }}>
            {viewText}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
